import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Radio, Mail, Lock, User as UserIcon } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";

export default function Login() {
  const { loginEmail, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await loginEmail(email, password);
      const nx = new URLSearchParams(location.search).get("next");
      navigate(location.state?.from || (nx && nx.startsWith("/") && !nx.startsWith("//") ? nx : "/dashboard"), { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-md mx-auto px-5 pt-24 pb-16">
      <div className="text-center mb-8">
        <Radio className="w-10 h-10 text-emerald-500 mx-auto" />
        <h1 className="fl-display text-3xl font-extrabold mt-3">WELCOME BACK</h1>
        <p className="text-slate-400 text-sm mt-1">Sign in to your Fliboe studio.</p>
      </div>

      <form onSubmit={submit} className="fl-card p-6 space-y-4">
        <label className="block">
          <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Email</span>
          <div className="relative mt-1.5">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="email" required className="fl-input pl-9" value={email} onChange={e => setEmail(e.target.value)} data-testid="login-email-input" placeholder="you@example.com" />
          </div>
        </label>
        <label className="block">
          <span className="fl-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Password</span>
          <div className="relative mt-1.5">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="password" required minLength={8} className="fl-input pl-9" value={password} onChange={e => setPassword(e.target.value)} data-testid="login-password-input" placeholder="••••••••" />
          </div>
        </label>
        {error && <div className="text-sm text-red-400 fl-mono text-xs" data-testid="login-error">{error}</div>}
        <button type="submit" disabled={busy} className="fl-btn w-full" data-testid="login-submit">{busy ? "Tuning…" : "Sign in"}</button>
        <div className="flex items-center justify-between text-xs">
          <Link to="/forgot-password" className="text-emerald-500 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          <Link to="/signup" className="text-slate-400 hover:text-white" data-testid="signup-link">Create an account →</Link>
        </div>
      </form>

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-white/10" />
        <span className="fl-mono text-[10px] tracking-widest uppercase text-slate-500">or</span>
        <div className="flex-1 h-px bg-white/10" />
      </div>

      <button onClick={loginWithGoogle} className="fl-btn ghost w-full" data-testid="google-signin-button">
        <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
        Continue with Google
      </button>
    </main>
  );
}
